/*
 * The admin session store: the admin-namespace JWT persisted in sessionStorage (flows/admin-auth).
 * Non-React so it is unit-testable and injected into the API client at the composition root —
 * components never read the token directly (the token lives outside React; nav permissions and the
 * must-reset gate come from the decoded claims, never the raw token). sessionStorage (not
 * localStorage) scopes the operator session to the tab: closing it drops the token, and logout is a
 * client-side clear because the JWT is stateless (there is no server revoke).
 */

const STORAGE_KEY = 'cou.adminToken';

/** The subset of the Web Storage API the admin session store needs (injectable for tests). */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AdminSessionStore {
  /** The stored admin token, or null if none is stored. */
  read(): string | null;
  /** Persist the admin token issued by login or the forced reset. */
  write(token: string): void;
  /** Drop the stored admin token (logout, or a 401 on any admin route). */
  clear(): void;
}

/**
 * An admin session store over a Web Storage backend. The backend is resolved lazily inside each
 * call: reading `window.sessionStorage` can itself throw in a locked-down browser, so resolving it
 * here (not as a default argument) keeps construction from throwing — the console still boots, the
 * token simply won't persist.
 */
export function createAdminSessionStore(storage?: KeyValueStorage): AdminSessionStore {
  const backend = (): KeyValueStorage => storage ?? window.sessionStorage;
  return {
    read() {
      try {
        const value = backend().getItem(STORAGE_KEY);
        return typeof value === 'string' && value.length > 0 ? value : null;
      } catch {
        return null;
      }
    },
    write(token) {
      try {
        backend().setItem(STORAGE_KEY, token);
      } catch {
        /* storage unavailable — the session just won't survive a reload */
      }
    },
    clear() {
      try {
        backend().removeItem(STORAGE_KEY);
      } catch {
        /* storage unavailable */
      }
    },
  };
}
